import React, { useState, useRef, useEffect } from 'react';
import {
  Box, Grid, Alert, Typography, CircularProgress,
  FormControlLabel, Checkbox,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useHistory, useRouteMatch, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import InvButton from '../../components/InvButton';
import { InvTextField, InvSelect } from '../../components/InvField';
import RfqLineItemsEditor from '../../components/material/RfqLineItemsEditor';
import RfqVendorSelector from '../../components/material/RfqVendorSelector';
import RfqAttachmentsPanel from '../../components/material/RfqAttachmentsPanel';
import RfqPublishReviewDialog from '../../components/material/RfqPublishReviewDialog';
import { getRfq, getRfqItems, updateRfqWithItemsAndVendors } from '../../api/rfq';
import useItemNames from '../../hooks/useItemNames';
import { RFQ_TYPES } from '../../constants/rfqManagement';
import {
  emptyRfqLine, toRfqUpdatePayload, fromApiRfq, fromApiRfqItem,
  toFormDateTime, validateRfqDates, normalizeApiList,
} from '../../utils/rfqEntity';
import { summarizeRfqInviteNotify } from '../../utils/rfqInviteNotify';
import { IPD_COLORS } from '../../theme';

const FORM_ID = 'rfq-edit-form';

export default function RfqEdit() {
  const { id } = useParams();
  const { url } = useRouteMatch();
  const history = useHistory();
  const listUrl = url.replace(/\/rfqs\/[^/]+\/edit$/, '/rfqs');
  const detailUrl = url.replace(/\/edit$/, '');

  const [loadingRfq, setLoadingRfq] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [existingPayload, setExistingPayload] = useState({});
  const [originalLines, setOriginalLines] = useState([]);
  const [rfqNumber, setRfqNumber] = useState('');

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [vendorError, setVendorError] = useState(null);
  const [lines, setLines] = useState([{ ...emptyRfqLine() }]);
  const [vendorIds, setVendorIds] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [reviewOpen, setReviewOpen] = useState(false);
  const submitModeRef = useRef('draft');

  const { getBaseUomCode, getPurchaseUomCode, getGstRate, resolveItemId } = useItemNames();

  const {
    control,
    handleSubmit,
    watch,
    trigger,
    reset,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      rfq_title: '',
      rfq_type: 'STANDARD',
      issue_date: dayjs().format('YYYY-MM-DDTHH:mm'),
      submission_deadline: dayjs().add(7, 'day').hour(18).minute(0).format('YYYY-MM-DDTHH:mm'),
      minimum_required_quotes: 3,
      is_open_bid: true,
      is_sealed_bid: false,
      terms_and_conditions: '',
      notes: '',
    },
  });

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoadingRfq(true);
      setLoadError(null);
      try {
        const [r, items] = await Promise.all([getRfq(id), getRfqItems(id)]);
        if (cancelled) return;
        const rfq = fromApiRfq(r);
        if (rfq.status !== 'DRAFT') {
          history.replace(detailUrl);
          return;
        }
        setRfqNumber(rfq.rfq_number);
        setExistingPayload(rfq.rfq_payload || {});
        setVendorIds(rfq.invited_vendor_ids || []);
        const itemList = normalizeApiList(items).map(fromApiRfqItem);
        setOriginalLines(itemList);
        setLines(itemList.length > 0 ? itemList : [{ ...emptyRfqLine() }]);
        reset({
          rfq_title: rfq.rfq_title || '',
          rfq_type: rfq.rfq_type || 'STANDARD',
          issue_date: toFormDateTime(rfq.issue_date) || dayjs().format('YYYY-MM-DDTHH:mm'),
          submission_deadline: toFormDateTime(rfq.submission_deadline)
            || dayjs().add(7, 'day').hour(18).minute(0).format('YYYY-MM-DDTHH:mm'),
          minimum_required_quotes: rfq.minimum_required_quotes ?? 3,
          is_open_bid: Boolean(rfq.is_open_bid),
          is_sealed_bid: Boolean(rfq.is_sealed_bid),
          terms_and_conditions: rfq.terms_and_conditions || '',
          notes: rfq.notes || '',
        });
      } catch (e) {
        if (!cancelled) setLoadError(e.message || 'Failed to load RFQ');
      } finally {
        if (!cancelled) setLoadingRfq(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [id, history, detailUrl, reset]);

  const formValues = watch();
  const submissionDeadline = formValues.submission_deadline;
  const typeOptions = RFQ_TYPES.map((t) => ({ value: t.value, label: t.label }));
  const validLineCount = lines.filter((l) => l.item_id && Number(l.required_quantity) > 0).length;
  const typeLabel = typeOptions.find((t) => t.value === formValues.rfq_type)?.label || formValues.rfq_type;

  function validateAll() {
    const dateErr = validateRfqDates(formValues.issue_date, formValues.submission_deadline);
    if (dateErr) return dateErr;
    if (validLineCount === 0) return 'Add at least one line item with item and quantity.';
    if (vendorIds.length === 0) {
      setVendorError('Select at least one approved vendor to invite.');
      return 'Select at least one approved vendor to invite.';
    }
    setSaveError(null);
    setVendorError(null);
    return null;
  }

  async function onSubmit(data) {
    const fieldsOk = await trigger(['rfq_title', 'rfq_type', 'issue_date', 'submission_deadline']);
    if (!fieldsOk) {
      const msg = 'Please fill all required RFQ fields.';
      setSaveError(msg);
      toast.error(msg);
      return;
    }
    const validationMsg = validateAll();
    if (validationMsg) {
      setSaveError(validationMsg);
      toast.error(validationMsg);
      return;
    }

    const publish = submitModeRef.current === 'publish';
    submitModeRef.current = 'draft';
    setSaving(true);
    setSaveError(null);

    try {
      const submitData = { ...data };
      if (publish && submitData.issue_date && dayjs(submitData.issue_date).isBefore(dayjs(), 'minute')) {
        submitData.issue_date = dayjs().format('YYYY-MM-DDTHH:mm');
      }
      const payload = toRfqUpdatePayload(submitData, { vendorIds, attachments, existingPayload, lines });
      const result = await updateRfqWithItemsAndVendors(id, payload, lines, originalLines, { publish });

      toast.success(publish
        ? `RFQ updated and published. ${summarizeRfqInviteNotify(result.notify)}`
        : 'RFQ draft saved.');

      history.push(`${detailUrl}${publish ? '?published=1' : '?saved=1'}`);
    } catch (e) {
      const msg = e.message || 'Failed to save RFQ';
      setSaveError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  async function openPublishReview() {
    const fieldsOk = await trigger(['rfq_title', 'rfq_type', 'issue_date', 'submission_deadline']);
    if (!fieldsOk) {
      const msg = 'Please fill all required RFQ fields.';
      setSaveError(msg);
      toast.error(msg);
      return;
    }
    const validationMsg = validateAll();
    if (validationMsg) {
      setSaveError(validationMsg);
      toast.error(validationMsg);
      return;
    }
    setReviewOpen(true);
  }

  async function confirmPublish() {
    submitModeRef.current = 'publish';
    await onSubmit(getValues());
  }

  if (loadingRfq) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress sx={{ color: IPD_COLORS.primary }} />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>
        <InvButton onClick={() => history.push(listUrl)}>Back to List</InvButton>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 10 }}>
      <PageHeader
        eyebrow="Material Management"
        title="Edit Request for Quotation"
        description={`Editing draft ${rfqNumber}`}
        actions={(
          <InvButton variant="outlined" tone="teal" startIcon={<ArrowBackIcon />}
            onClick={() => history.push(detailUrl)}>
            Cancel
          </InvButton>
        )}
      />

      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      )}

      <Box component="form" id={FORM_ID} noValidate onSubmit={handleSubmit(onSubmit)}
        sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <SectionCard title="RFQ Details" subtitle="Title, dates, and commercial terms">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <InvTextField label="RFQ Number" value={rfqNumber} disabled />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Controller name="rfq_type" control={control}
                rules={{ required: 'Required' }}
                render={({ field }) => (
                  <InvSelect label="RFQ Type *" value={field.value} onChange={field.onChange}
                    options={typeOptions} error={errors.rfq_type} />
                )} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Controller name="minimum_required_quotes" control={control}
                render={({ field }) => (
                  <InvTextField
                    {...field}
                    label="Minimum Quotes"
                    type="number"
                    inputProps={{ min: 1, step: 1 }}
                    helperText="Must be at least 1. Default is 3."
                  />
                )} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller name="is_open_bid" control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={Boolean(field.value)}
                        onChange={(e) => field.onChange(e.target.checked)}
                        sx={{ color: IPD_COLORS.primary }}
                      />
                    )}
                    label={(
                      <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                          Open bid — allow unregistered vendors to bid
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
                          The public RFQ link will offer new vendors a quick registration.
                        </Typography>
                      </Box>
                    )}
                  />
                )} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller name="is_sealed_bid" control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={Boolean(field.value)}
                        onChange={(e) => field.onChange(e.target.checked)}
                        sx={{ color: IPD_COLORS.primary }}
                      />
                    )}
                    label={(
                      <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                          Sealed bid — hide rates until the deadline
                        </Typography>
                      </Box>
                    )}
                  />
                )} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller name="rfq_title" control={control}
                rules={{ required: 'Title is required' }}
                render={({ field }) => (
                  <InvTextField {...field} label="RFQ Title *" error={errors.rfq_title} />
                )} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Controller name="issue_date" control={control}
                rules={{ required: 'Required' }}
                render={({ field }) => (
                  <InvTextField {...field} label="Issue Date *" type="datetime-local"
                    error={errors.issue_date} />
                )} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Controller name="submission_deadline" control={control}
                rules={{ required: 'Required' }}
                render={({ field }) => (
                  <InvTextField {...field} label="Submission Deadline *" type="datetime-local"
                    error={errors.submission_deadline} />
                )} />
            </Grid>
            <Grid item xs={12}>
              <Controller name="terms_and_conditions" control={control}
                render={({ field }) => (
                  <InvTextField {...field} label="Terms & Conditions" multiline rows={4} />
                )} />
            </Grid>
            <Grid item xs={12}>
              <Controller name="notes" control={control}
                render={({ field }) => (
                  <InvTextField {...field} label="Internal Notes" multiline rows={2} />
                )} />
            </Grid>
          </Grid>
        </SectionCard>

        <RfqLineItemsEditor
          lines={lines}
          onChange={setLines}
          getBaseUomCode={getBaseUomCode}
          getPurchaseUomCode={getPurchaseUomCode}
          getGstRate={getGstRate}
          defaultRequiredByDate={submissionDeadline}
        />

        <RfqVendorSelector
          selectedIds={vendorIds}
          onChange={setVendorIds}
          error={vendorError}
        />

        <RfqAttachmentsPanel attachments={attachments} onChange={setAttachments} />

        <Box sx={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 10,
          borderTop: `1px solid ${IPD_COLORS.border}`,
          backgroundColor: 'rgba(255,255,255,0.96)',
          backdropFilter: 'blur(8px)',
          px: { xs: 2, sm: 3 }, py: 1.5,
        }}>
          <Box sx={{
            maxWidth: 1400, mx: 'auto',
            display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
            gap: 1, flexWrap: 'wrap',
          }}>
            <InvButton type="submit" variant="outlined" tone="teal"
              loading={saving && !reviewOpen} onClick={() => { submitModeRef.current = 'draft'; }}>
              Save Draft
            </InvButton>
            <InvButton type="button" tone="mint"
              disabled={saving} onClick={openPublishReview}>
              Publish RFQ
            </InvButton>
          </Box>
        </Box>
      </Box>

      <RfqPublishReviewDialog
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        onPublish={confirmPublish}
        loading={saving}
        error={saveError}
        formId={FORM_ID}
        rfqNumber={rfqNumber}
        title={formValues.rfq_title}
        typeLabel={typeLabel}
        issueDate={formValues.issue_date}
        submissionDeadline={formValues.submission_deadline}
        minimumQuotes={formValues.minimum_required_quotes}
        validLineCount={validLineCount}
        vendorCount={vendorIds.length}
        isOpenBid={formValues.is_open_bid}
        attachmentCount={attachments.filter((a) => a.file_name).length}
        itemLines={lines
          .filter((l) => l.item_id && l.required_quantity)
          .map((l) => `${resolveItemId(l.item_id)} — Qty ${l.required_quantity} ${l.uom_code || ''}`)}
      />
    </Box>
  );
}
